import React, { createContext, useContext, useReducer, useEffect, useCallback, type ReactNode } from 'react';
import type { NormalizedResume } from '../models/resume-draft';
import { resumeService, type ResumeVersion } from '../services/resumeService';
import { toast } from 'react-hot-toast';

// --- Types ---
interface EditorState {
    resumeId: string | null;
    resumeName: string | null; // Added
    currentVersion: ResumeVersion | null;
    history: {
        past: ResumeVersion[];
        future: ResumeVersion[];
    };
    isDirty: boolean; // Has unsaved local changes compared to currentVersion
    isSaving: boolean;
    lastSavedVersionId: string | null;
    canvasRef: React.RefObject<HTMLDivElement | null>;
}

// --- Actions ---
type Action =
    | { type: 'LOAD_VERSION'; payload: { version: ResumeVersion } }
    | { type: 'UPDATE_LOCAL'; payload: { resume: NormalizedResume; source: string } }
    | { type: 'UNDO' }
    | { type: 'REDO' }
    | { type: 'SAVE_START' }
    | { type: 'SAVE_SUCCESS'; payload: { version: ResumeVersion } }
    | { type: 'SAVE_ERROR' };

// --- State ---
const INITIAL_STATE: EditorState = {
    resumeId: null,
    resumeName: null, // Added
    currentVersion: null,
    history: { past: [], future: [] },
    isDirty: false,
    isSaving: false,
    lastSavedVersionId: null,
    canvasRef: { current: null } // Initial dummy ref, will be overwritten by Provider's specialized ref if we want, OR we use a useRef in the Provider.
};

// --- Reducer ---
function editorReducer(state: EditorState, action: Action): EditorState {
    switch (action.type) {
        case 'LOAD_VERSION':
            return {
                ...state,
                resumeId: action.payload.version.resume_id,
                resumeName: action.payload.version.name || null, // Extract name
                currentVersion: action.payload.version,
                history: { past: [], future: [] },
                isDirty: false,
                lastSavedVersionId: action.payload.version.version_id
            };

        case 'UPDATE_LOCAL': {
            if (!state.currentVersion) return state;

            // In this simplified versionless-edits model:
            // We treat the "current" state as a potential next version.
            // For undo/redo, we might want to stash the *previous* state in 'past'.

            // NOTE: The previous code was pushing `currentVersion` to `past`.
            // But `currentVersion` is the SERVER state.
            // If we are making local edits, we technically are creating a "floating" version.

            // To keep it simple: We will clone the currentVersion but updated with new JSON.
            // This effectively behaves like a new version locally.

            const newPast = [...state.history.past, state.currentVersion];
            if (newPast.length > 50) newPast.shift();

            const newVersion: ResumeVersion = {
                ...state.currentVersion,
                resume_json: action.payload.resume,
                source: action.payload.source,
                // We don't change version_id yet because it's not saved
            };

            return {
                ...state,
                currentVersion: newVersion,
                history: {
                    past: newPast,
                    future: []
                },
                isDirty: true
            };
        }

        case 'UNDO': {
            if (state.history.past.length === 0 || !state.currentVersion) return state;

            const previous = state.history.past[state.history.past.length - 1];
            const newPast = state.history.past.slice(0, -1);
            const newFuture = [state.currentVersion, ...state.history.future];

            return {
                ...state,
                currentVersion: previous,
                history: {
                    past: newPast,
                    future: newFuture
                },
                isDirty: true // Undoing to a previous state is technically a change from "latest saved"
            };
        }

        case 'REDO': {
            if (state.history.future.length === 0 || !state.currentVersion) return state;

            const next = state.history.future[0];
            const newFuture = state.history.future.slice(1);
            const newPast = [...state.history.past, state.currentVersion];

            return {
                ...state,
                currentVersion: next,
                history: {
                    past: newPast,
                    future: newFuture
                },
                isDirty: true
            };
        }

        case 'SAVE_START':
            return { ...state, isSaving: true };

        case 'SAVE_SUCCESS':
            return {
                ...state,
                isSaving: false,
                isDirty: false,
                currentVersion: action.payload.version, // Update with real server version (new ID)
                lastSavedVersionId: action.payload.version.version_id
            };

        case 'SAVE_ERROR':
            return { ...state, isSaving: false };

        default:
            return state;
    }
}

// --- Context ---
interface EditorContextType extends EditorState {
    loadResume: (resumeId: string) => Promise<void>;
    updateResume: (resume: NormalizedResume, source: string) => void;
    undo: () => void;
    redo: () => void;
    save: () => Promise<void>;
    canUndo: boolean;
    canRedo: boolean;
}

const EditorContext = createContext<EditorContextType | undefined>(undefined);

export function EditorProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(editorReducer, INITIAL_STATE);
    const canvasRef = React.useRef<HTMLDivElement>(null); // Actual Ref creation

    const loadResume = useCallback(async (resumeId: string) => {
        try {
            const version = await resumeService.getLatestVersion(resumeId);
            if (version) {
                dispatch({ type: 'LOAD_VERSION', payload: { version } });
            } else {
                toast.error("Resume not found");
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to load resume");
        }
    }, []);

    const updateResume = useCallback((resume: NormalizedResume, source: string) => {
        dispatch({ type: 'UPDATE_LOCAL', payload: { resume, source } });
    }, []);

    const undo = useCallback(() => dispatch({ type: 'UNDO' }), []);
    const redo = useCallback(() => dispatch({ type: 'REDO' }), []);

    const save = useCallback(async () => {
        if (!state.currentVersion || !state.resumeId) return;

        dispatch({ type: 'SAVE_START' });
        try {
            const savedVersion = await resumeService.saveVersion(
                state.resumeId,
                state.currentVersion.resume_json,
                state.currentVersion.source
            );
            dispatch({ type: 'SAVE_SUCCESS', payload: { version: savedVersion } });
        } catch (e) {
            console.error(e);
            dispatch({ type: 'SAVE_ERROR' });
            toast.error("Failed to save changes");
        }
    }, [state.currentVersion, state.resumeId]);

    // Autosave Effect
    useEffect(() => {
        if (state.isDirty && !state.isSaving) {
            const timer = setTimeout(() => {
                save();
            }, 2000); // 2 second debounce

            return () => clearTimeout(timer);
        }
    }, [state.isDirty, state.isSaving, save]);

    return (
        <EditorContext.Provider value={{
            ...state,
            loadResume,
            updateResume,
            undo,
            redo,
            save,
            canvasRef,
            canUndo: state.history.past.length > 0,
            canRedo: state.history.future.length > 0
        }}>
            {children}
        </EditorContext.Provider>
    );
}

export function useEditor() {
    const context = useContext(EditorContext);
    if (!context) throw new Error("useEditor must be used within EditorProvider");
    return context;
}
